/**
 * A second, throwaway Supabase client that keeps nothing: its session lives in
 * memory only, is never written to storage, and never refreshes.
 *
 * For the one case where a person must prove who they are again WITHOUT
 * disturbing the session the app is running on — the Platform Admin site's
 * "Add Platform Admin" (src/lib/superAdminService.ts addPlatformAdmin).
 *
 * Why not sign in again on the main client (src/lib/supabaseClient.ts): its
 * fetch wrapper treats any 400 from /auth/v1/token as a dead refresh token,
 * wipes the stored session and signs out — and a mistyped password is a 400
 * from that same endpoint. A wrong password there logs the person out of the
 * app (seen in a browser, 2026-10-01).
 *
 * It uses its own storage key so it never touches the main client's session.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';

export function createEphemeralSupabaseClient(): SupabaseClient {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are required');
  }
  return createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: 'pp-ephemeral-identity-check',
    },
  });
}
